import { CamelCasedProperties, CamelCasedPropertiesDeep } from 'type-fest';
import { z } from 'zod/v4';
import {
  calculationTypeSchema,
  createDefinitionSchema,
  createThresholdOptionsSchema,
  stepOptionsSchema,
  unitOptionsSchema,
} from '../../utils';

export type StatChartSparklineOptions = z.infer<typeof statChartSparklineSchema>;
const statChartSparklineSchema = z.object({
  color: z.string().optional(),
  width: z.number().optional(),
  show_thresholds: z.boolean().optional(),
});

const statChartStepOptionsSchema = stepOptionsSchema.extend({
  show_value: z.boolean().optional(),
  background_color: z.string().optional(),
});

export const statChartThresholdOptionsSchema = createThresholdOptionsSchema(statChartStepOptionsSchema).extend({
  base: statChartStepOptionsSchema.omit({ value: true }).optional(),
});

export const statChartShowSeriesNameSchema = z.enum(['Auto', 'Always', 'Never']);

export interface StatChartStepOption extends z.TypeOf<typeof statChartStepOptionsSchema> {}
export interface StatChartThresholdOptions extends z.TypeOf<typeof statChartThresholdOptionsSchema> {}

/**
 * Type for the spec-driven `DashboardPanel` variant of the stat chart.
 */
export interface StatChartSpec extends z.TypeOf<typeof statChartSpecSchema> {}

/**
 * Zod schema for the spec-driven `DashboardPanel` variant of the stat chart.
 */
export const statChartSpecSchema = z.object({
  // TODO: remove calculation since no longer used
  calculation: calculationTypeSchema.describe('DEPRECATED').optional(),
  unit: unitOptionsSchema,
  thresholds: statChartThresholdOptionsSchema.optional(),
  sparkline: statChartSparklineSchema.optional(),
  // TODO(bg): Move over FontSizeOption from FontSizeSelector
  value_font_size: z.number().optional(),
  show_series_name: statChartShowSeriesNameSchema.optional(),
});

/**
 * The schema for a StatChart `DashboardPanel` definition.
 */
export interface StatChartDefinition extends z.TypeOf<typeof statChartDefinitionSchema> {}
export const statChartDefinitionSchema = createDefinitionSchema('StatChart', statChartSpecSchema);

/**
 * Version of the Zod schema to be used for base types for the props-driven `Viz` variant of the stat chart.
 *
 * Currently, it is the same as the spec-driven variant, but it may need to change in the future to
 * do things like flatten a specific property or some other transform (examples can be found in pie chart).
 */
export const statChartVizSchema = statChartSpecSchema;

type StatChartVizSchemaType = z.TypeOf<typeof statChartVizSchema>;

/**
 * Enum of stat chart properties that should only be camelCased shallowly when translated for the `Viz` use case.
 */
type StatChartShallowCamelProperties = 'thresholds' | 'sparkline' | 'unit';

/**
 * Base type for the props-driven `Viz` variant of the stat chart generated from the Zod schema and
 * converting some snake_case properties to camelCase. This interface should be extended in `viz-framework`
 * to do the following:
 * - Add additional types for `Viz` only use cases.
 * - Extend properties to add more meaningful jsdoc. This is necessary because both `z.Typeof` and
 *   `CamelCasedPropertiesDeep` lose jsdoc.
 */
export interface StatChartPropsBase
  extends CamelCasedPropertiesDeep<Omit<StatChartVizSchemaType, StatChartShallowCamelProperties>>,
    CamelCasedProperties<Pick<StatChartVizSchemaType, StatChartShallowCamelProperties>> {}
